import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, X, Loader2, Plus, Trash2, ShoppingCart } from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../../lib/api.js'
import { fetchInventoryMaster, rows, stockDefaultBranch } from '../../lib/stock.js'
import { relabelBranch } from '../../lib/ticket.js'
import { buildRequestPayload } from '../../lib/purchase.js'
import QtyStepper from './QtyStepper.jsx'
import { useTypeahead } from '../../lib/useTypeahead.js'

// สร้างคำขอซื้อ — raise one or more stage-1 request lines. Item-first, like ตัด
// Stock: search the master, add items to a cart (each with a quantity), pick the
// branch the request is for, then submit. A multi-item request is N sequential
// POSTs to /api/purchase (POST writes ONE inventory_request row) — sequential so
// a failure at #3 leaves #1-2 durable, and committed items leave the cart so a
// retry can't double-create.

export default function PurchaseCreateDrawer({ branches, defaultBranchId, onClose, onSaved }) {
  const [branchId, setBranchId] = useState(() => stockDefaultBranch(null, defaultBranchId) || '')
  const [q, setQ] = useState('')
  const [master, setMaster] = useState([])
  const [loadingMaster, setLoadingMaster] = useState(true)
  const [cart, setCart] = useState([])           // [{ inventory_id, name, unit, qty }]
  const [remark, setRemark] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const searchRef = useRef(null)

  useEffect(() => { searchRef.current?.focus() }, [])

  useEffect(() => {
    let alive = true
    setLoadingMaster(true)
    fetchInventoryMaster(apiGet)
      .then((items) => { if (alive) setMaster(items) })
      .catch(() => { if (alive) setError('โหลดรายการของไม่สำเร็จ') })
      .finally(() => { if (alive) setLoadingMaster(false) })
    return () => { alive = false }
  }, [])

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return []
    return master
      .filter((it) => String(it.Name || '').toLowerCase().includes(needle))
      .slice(0, 30)
  }, [q, master])

  const inCart = (id) => cart.some((c) => c.inventory_id === id)
  // Items already in the cart stay visible but unpickable, so Enter skips them.
  const ta = useTypeahead({
    count: results.length,
    onSelect: (i) => { const it = results[i]; if (it && !inCart(it.id)) addItem(it) },
    onClose: () => setQ(''),
  })
  const addItem = (it) => {
    if (inCart(it.id)) return
    setCart((c) => [...c, { inventory_id: it.id, name: it.Name, unit: it.unit || '', qty: '1' }])
    setQ('')
    searchRef.current?.focus()
  }
  const setQty = (id, qty) => setCart((c) => c.map((x) => x.inventory_id === id ? { ...x, qty } : x))
  const removeItem = (id) => setCart((c) => c.filter((x) => x.inventory_id !== id))

  const validCart = cart.filter((c) => Math.trunc(Number(c.qty) || 0) > 0)
  const canSave = !!branchId && validCart.length > 0 && !saving

  async function save() {
    if (!canSave) return
    setSaving(true)
    setError(null)
    let done = 0
    try {
      for (const item of validCart) {
        await postJson('/api/purchase', buildRequestPayload({
          inventory_id: item.inventory_id,
          branch_id: branchId,
          qty: item.qty,
          remark,
        }))
        done += 1
        // Drop committed items so a retry after a mid-list failure can't double-create.
        setCart((c) => c.filter((x) => x.inventory_id !== item.inventory_id))
      }
      onSaved?.(done)
    } catch (e) {
      setError(`${e.message || 'บันทึกไม่สำเร็จ'} (บันทึกสำเร็จ ${done}/${validCart.length} รายการ)`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={() => !saving && onClose?.()} />
      <div className="relative w-full max-w-[520px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center"><ShoppingCart size={18} /></div>
            <div>
              <div className="text-[15.5px] font-semibold text-slate-900">สร้างคำขอซื้อ</div>
              <div className="text-[12.5px] text-slate-500">เลือกของที่ต้องการสั่งซื้อและจำนวน</div>
            </div>
          </div>
          <button onClick={() => !saving && onClose?.()} className="p-1.5 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* branch */}
          <label className="block">
            <span className="text-[12.5px] font-medium text-slate-500">สาขาที่ขอ</span>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800">
              <option value="">— เลือกสาขา —</option>
              {rows(branches).map((b) => (
                <option key={b.id} value={b.id}>{relabelBranch(b.name || b.Name) || `สาขา ${b.id}`}</option>
              ))}
            </select>
          </label>

          {/* search */}
          <div>
            <span className="text-[12.5px] font-medium text-slate-500">ค้นหาของ</span>
            <div className="relative mt-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={ta.onKeyDown}
                placeholder={loadingMaster ? 'กำลังโหลดรายการของ…' : 'พิมพ์ชื่อของ…'} disabled={loadingMaster}
                className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-9 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 focus:border-brand-300 focus:ring-2 focus:ring-brand-100 outline-none" />
              {q && <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={15} /></button>}
            </div>
            {q && (
              <div ref={ta.listRef} className="mt-1.5 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 max-h-[240px] overflow-y-auto">
                {results.length === 0 && <div className="px-4 py-3 text-[13px] text-slate-400">ไม่พบของที่ตรงกับ “{q}”</div>}
                {results.map((it, i) => (
                  <button key={it.id} data-ti-index={i}
                    onMouseEnter={() => ta.setActiveIndex(i)}
                    onMouseDown={(e) => { e.preventDefault(); if (!inCart(it.id)) addItem(it) }}
                    disabled={inCart(it.id)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                      i === ta.activeIndex ? 'bg-brand-50' : 'hover:bg-brand-50'}`}>
                    <div className="min-w-0">
                      <div className="text-[13.5px] text-slate-800 truncate">{it.Name}</div>
                      <div className="text-[11.5px] text-slate-400 truncate">{[it.mainCategory, it.subCategory].filter(Boolean).join(' · ')}</div>
                    </div>
                    {inCart(it.id) ? <span className="text-[12px] text-brand-600">เพิ่มแล้ว</span> : <Plus size={16} className="text-brand-500 shrink-0" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* cart */}
          <div>
            <div className="text-[12.5px] font-medium text-slate-500 mb-1.5">รายการที่จะขอ ({cart.length})</div>
            {cart.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white/50 px-4 py-8 text-center text-[13px] text-slate-400">
                ค้นหาและเลือกของด้านบนเพื่อเพิ่มลงรายการ
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                {cart.map((c) => (
                  <div key={c.inventory_id} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] text-slate-800 truncate">{c.name}</div>
                      {c.unit && <div className="text-[11.5px] text-slate-400">หน่วย: {c.unit}</div>}
                    </div>
                    <QtyStepper value={c.qty} onChange={(v) => setQty(c.inventory_id, v)} />
                    <button onClick={() => removeItem(c.inventory_id)} className="p-1.5 text-slate-300 hover:text-rose-600"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* remark */}
          <label className="block">
            <span className="text-[12.5px] font-medium text-slate-500">หมายเหตุ (ไม่บังคับ)</span>
            <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2}
              placeholder="เหตุผล / รายละเอียดเพิ่มเติม"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 resize-none" />
          </label>

          {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{error}</div>}
        </div>

        {/* footer */}
        <div className="px-5 py-4 bg-white border-t border-slate-200 flex items-center justify-end gap-3">
          <button onClick={() => !saving && onClose?.()} className="rounded-xl px-4 py-2 text-[14px] text-slate-600 hover:bg-slate-100">ยกเลิก</button>
          <button onClick={save} disabled={!canSave}
            className="rounded-xl bg-brand-600 px-5 py-2 text-[14px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-2">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            สร้างคำขอ {validCart.length > 0 ? `(${validCart.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}
